import { SearchIcon } from "frontend/icons/search";
import React, { useRef, useState, useEffect, useCallback, PropsWithChildren } from "react";
import { VariableSizeList } from "react-window";
import { useAtom, useSetAtom } from "jotai";
import { isDraggingElementAtom, sidePanelType, SidepanelType } from "state-atoms";
import styles from "./shapes-side-panel.module.css";
import { useCdn } from "frontend/hooks/caching/cdn/use-cdn";
import { ShapeSideMenuIcon } from "frontend/icons/shape-side-menu-icon";
import { XIcon } from "frontend/icons/x-icon";
import { ShapeLibraries } from "frontend/canvas-designer-new/shape-libraries/types";
import { SearchField } from "frontend/ui-components/search-field/search-field";
import useVirtualizedShapeLibraries, { ROW_HEIGHTS } from "./hooks/use-virtualized-shape-libraries";
import VirtualizedRow, { SelectedItem, ShapeInfo } from "./virtualized-list/virtualized-row";
import { LibraryStatesProvider, useLibraryStates } from "./context/library-context";
import useTrackSearch from "frontend/hooks/use-track-search";
import { CursorType, getCursorTypeForElementType } from "frontend/canvas-designer-new/cursor-type";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { useHover } from "frontend/ui-components/floaters/use-hover";
import ShapesNoResults from "./virtualized-list/shapes-no-results";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { Box } from "frontend/ui-components/design-system/box/box";
import cn from "classnames";
import { useOnClickOutside } from "usehooks-ts";
import { CloseIcon } from "frontend/icons/close";
import { TextInput } from "frontend/ui-components/design-system/text-input";

type CIMShapesSidePanelProps = {
  onClose: () => void;
};

const CIMShapesSidePanel = ({ children, onClose }: PropsWithChildren<CIMShapesSidePanelProps>) => {
  const floaterRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(floaterRef, () => {
    onClose();
  });

  return (
    <Box variant="modal" className={cn(styles.cimShapesSidePanel)} ref={floaterRef}>
      <div className={styles.closeIcon} onClick={onClose}>
        <CloseIcon onClick={onClose} color="#848199" />
      </div>
      {children}
    </Box>
  );
};

function ShapesSidePanel({
  cursorType,
  onMenuItemClick,
  onUpgradeRequired,
  onMenuItemDragEnd,
}: {
  cursorType: CursorType;
  onMenuItemClick: (elementType: string, elementData: Record<string, unknown>, elementInfo: ShapeInfo) => void;
  onUpgradeRequired?: () => void;
  onMenuItemDragEnd?: () => void;
}) {
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const setOpenedSidePanel = useSetAtom(sidePanelType);
  const [isDraggingElement, setIsDraggingElement] = useAtom(isDraggingElementAtom);

  const isFirstRender = useRef(true);
  const listRef = useRef<VariableSizeList>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const closeIconRef = useRef<HTMLDivElement>(null);
  const showCloseTooltip = useHover(closeIconRef);

  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);

  useEffect(() => {
    if (selectedItem && selectedItem.cursorType !== cursorType) {
      setSelectedItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursorType]);

  const onItemClick = useCallback(
    (elementType: string, elementData: Record<string, unknown>, elementInfo: ShapeInfo) => {
      setSelectedItem({
        cursorType: getCursorTypeForElementType(elementType),
        libraryId: elementInfo.library,
        groupId: elementInfo.group,
        itemId: elementInfo.item,
      });
      onMenuItemClick(elementType, elementData, elementInfo);
    },
    [onMenuItemClick]
  );

  const [isRetrying, setIsRetrying] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  useTrackSearch(searchTerm, "shape-side-panel-search", {
    searchTerm,
    searchTermLength: searchTerm.length,
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    for (const libraryId of Object.keys(libraries)) {
      setLibraryExpansion(libraryId, !!searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const [listHeight, setListHeight] = useState(0);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const updateHeight = () => {
      if (wrapperRef.current) {
        const margin = 10;
        setListHeight(wrapperRef.current.clientHeight - margin);
      }
    };

    updateHeight();

    const wrapper = wrapperRef.current;
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(wrapper);

    return () => {
      if (wrapper) {
        resizeObserver.unobserve(wrapper);
      }
    };
  }, []);

  const { data: libraries, isLoading, error, mutate } = useCdn<ShapeLibraries>("/shape-libraries/libraries.json");
  const { setLibraryExpansion } = useLibraryStates();

  const rows = useVirtualizedShapeLibraries(libraries, searchTerm, !!onUpgradeRequired);
  const getRowHeight = (index: number) => ROW_HEIGHTS[rows[index].type];

  const paddingTop = document.getElementById("app-header-container")?.clientHeight || 64;

  const showNoResults = !isLoading && !error && searchTerm && rows.length === 0;

  const handleRetry = () => {
    setIsRetrying(true);
    mutate();
    setTimeout(() => {
      setIsRetrying(false);
    }, 10000);
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [rows]);

  const Content = (
    <>
      <div className={styles.title}>
        <ShapeSideMenuIcon width={25} height={25} />
        <span className={styles.titleText}>Shapes</span>
      </div>
      <div className={styles.searchContainer}>
        {isCimReorderEnabled ? (
          <TextInput
            leftIcon={<SearchIcon scale={1.2} color="#7F869B" />}
            variant="small"
            placeholder="Search shapes"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        ) : (
          <SearchField
            type="search"
            searchValue={searchTerm}
            setSearchValue={setSearchTerm}
            placeholder="Search shapes"
          />
        )}
      </div>
      <div className={styles.divider} />
      {error && (
        <div className={styles.errorContainer}>
          <span className={styles.errorText}>Something went wrong when loading your libraries.</span>
          <button className={styles.reloadButton} onClick={handleRetry} disabled={isRetrying}>
            {isRetrying ? "Retrying..." : "Try again"}
          </button>
        </div>
      )}
      {showNoResults && <ShapesNoResults searchTerm={searchTerm} />}
      {!isLoading && !error && (
        <div className={styles.listWrapper} ref={wrapperRef}>
          <VariableSizeList
            ref={listRef}
            height={listHeight}
            width={isCimReorderEnabled ? "100%" : "100%"}
            itemCount={rows.length}
            itemSize={getRowHeight}
            itemData={{
              rows,
              selectedItem,
              onMenuItemClick: onItemClick,
              onUpgradeRequired,
            }}
            itemKey={(index) => {
              const row = rows[index];
              if (row.type === "library-header") {
                return row.libraryId;
              }
              return `${row.libraryId}-${row.groupId}-${row.rowIndex}`;
            }}
          >
            {VirtualizedRow}
          </VariableSizeList>
        </div>
      )}
    </>
  );

  if (isCimReorderEnabled) {
    return <CIMShapesSidePanel onClose={() => setOpenedSidePanel(SidepanelType.none)}>{Content}</CIMShapesSidePanel>;
  }

  return (
    <div
      className={styles.container}
      data-dragging={isDraggingElement ? "true" : "false"}
      style={{
        paddingTop,
        ...({ "--drag-cursor": isDraggingElement ? "grabbing" : "inherit" } as React.CSSProperties),
      }}
      onMouseUp={() => {
        if (isDraggingElement) {
          onMenuItemDragEnd?.();
          setIsDraggingElement(false);
        }
      }}
    >
      <div className={styles.closeIcon} style={{ paddingTop }} ref={closeIconRef}>
        <XIcon width={25} height={25} color="#848199" onClick={() => setOpenedSidePanel(SidepanelType.none)} />
      </div>
      {showCloseTooltip && (
        <Tooltip
          relativeTo={closeIconRef}
          label="Close"
          side="bottom"
          boundary="none"
          customStyle={{ marginTop: -20, marginLeft: -6 }}
        />
      )}
      {Content}
      {/* Future: Add manage shapes menu */}
      {/* <div className={styles.buttonContainer}>
        <button className={styles.openModalButton}>Manage shapes</button>
      </div> */}
    </div>
  );
}

const ShapesSidePanelWrapper = React.memo(function (props: React.ComponentProps<typeof ShapesSidePanel>) {
  return (
    <LibraryStatesProvider>
      <ShapesSidePanel {...props} />
    </LibraryStatesProvider>
  );
});

ShapesSidePanelWrapper.displayName = "ShapesSidePanelWrapper";

export default ShapesSidePanelWrapper;
